/**
 * @genesis_domain Auth Engine
 * @genesis_version V9.1
 * @genesis_intent Signup page thin orchestrator — delegates to RegistrationForm domain component.
 *   Preserves Bento UI marketing panel on the right side.
 * @genesis_boundaries No business logic, no state management, no API calls. Pure layout orchestration.
 * @genesis_ai_trail V1.0: Refactored from 227-line monolith with inline Zod/RHF to thin orchestrator.
 *   Registration logic extracted to RegistrationForm + useRegistrationViewModel.
 *   V18.0: Z-Axis Standardization Strike — eradicated all literal Tailwind colors.
 */

import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';

import { RegistrationForm } from '@/domains/auth/components/RegistrationForm';

export function Signup() {
  return (
    <div className="h-full w-full bg-transparent flex items-center justify-center p-6">
      <div className="w-full max-w-[1200px] grid grid-cols-1 lg:grid-cols-2 theme-card backdrop-blur-3xl border border-[var(--color-glass-border)] shadow-2xl rounded-3xl overflow-hidden">
        {/* Left Side - Registration Form */}
        <div className="p-8 lg:p-16 flex flex-col justify-center bg-transparent">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <RegistrationForm />
          </motion.div>
        </div>

        {/* Right Side - Visual/Marketing */}
        <div className="hidden lg:flex relative bg-[var(--color-primary-subtle)] backdrop-blur-2xl border-l border-[var(--color-glass-border)] p-16 flex-col justify-between text-[var(--color-content-primary)] overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.2)_0%,transparent_50%)]" />
          </div>

          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--color-surface-elevated)] backdrop-blur-md rounded-full text-xs font-bold uppercase tracking-widest mb-8 text-[var(--color-glass-text)]"
            >
              <Heart size={14} className="text-[var(--color-glass-text)] opacity-60" />
              Your Culinary Legacy Begins Here
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-5xl font-bold leading-tight mb-6"
            >
              Preserve the recipes <br /> that define your <br /> family story.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-[var(--color-glass-text)] opacity-80 text-lg font-medium max-w-md italic"
            >
              "From grandma's secret sauce to dad's famous chili, every recipe tells a story worth
              preserving for generations."
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="relative z-10 bg-[var(--color-surface-elevated)] backdrop-blur-xl rounded-2xl p-8 border border-[var(--color-glass-border)]"
          >
            <div className="flex items-center gap-6 mb-8">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map(familyNum => (
                  <div
                    key={`family-avatar-${familyNum}`}
                    className="w-10 h-10 rounded-full border-2 border-[var(--color-primary-base)]/30 bg-[var(--color-surface-elevated)] overflow-hidden"
                  >
                    <img
                      src={`https://picsum.photos/seed/family${familyNum}/100/100`}
                      alt="Family"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ))}
              </div>
              <div>
                <p className="font-bold text-[var(--color-glass-text)]">Personal Vaults Created</p>
                <p className="text-xs text-[var(--color-glass-text)] opacity-60">5,842 Recipes Preserved</p>
              </div>
            </div>
            <div className="h-2 w-full bg-[var(--color-surface-elevated)] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '85%' }}
                transition={{ delay: 1, duration: 1.5 }}
                className="h-full bg-[var(--color-primary-base)] rounded-full"
              />
            </div>
          </motion.div>
          <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-[var(--color-surface-elevated)] rounded-full blur-[100px] opacity-50" />
          <div className="absolute -top-20 -left-20 w-60 h-60 bg-[var(--color-surface-elevated)] rounded-full blur-[80px] opacity-30" />
        </div>
      </div>
    </div>
  );
}